import axios from "axios";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import * as FaIcons from "react-icons/fa";
import { refreshMenuCache } from "../api/menuApi";

import "react-toastify/dist/ReactToastify.css";

import { notifyUser } from "../utils/notifyUser";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api/menu`;

// Get all FA icon keys
const availableFaIcons = Object.keys(FaIcons).filter((key) =>
    key.startsWith("Fa"),);

const AdminCreateMenu = () => {
    const [title, setTitle] = useState("");
    const [itemType, setItemType] = useState("section");
    const [parentId, setParentId] = useState(null);
    const [icon, setIcon] = useState("");
    const [iconSearch, setIconSearch] = useState("");
    const [showDropdown, setShowDropdown] = useState(false);
    const [menus, setMenus] = useState([]);

    useEffect(() => {
        const fetchAllMenus = async () => {
            try {
                const response = await axios.get(`${API_URL}/master/`, { withCredentials: true });
                setMenus(response.data);
            } catch(error) {
                console.error("Failed to fetch menus", error);
            }
        };
        fetchAllMenus();
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if(event.target.closest(".icon-picker") === null) {
                setShowDropdown(false);
            }
        };

        document.addEventListener("click", handleClickOutside);

        return () => {
            document.removeEventListener("click", handleClickOutside);
        };
    }, []);

    const filteredIcons = availableFaIcons.filter((iconName) =>
        iconName.toLowerCase().includes(iconSearch.toLowerCase()),);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if(!title || !itemType)
            return notifyUser(null, "error", "Title & Type are required!", "toast", null, null, null, null, null, false, false, "top-center");

        try {
            const localUser = JSON.parse(localStorage.getItem("user"));
            const currentUser =
                localUser?.username || localUser?.id || "unknown";

            const res = await axios.post(`${API_URL}/master/`, {
                title,
                parent_id: parentId || null,
                icon,
                item_type: itemType,
                user: currentUser,
            }, { withCredentials: true });
            // console.log(" Response:", res.data);
            notifyUser(null, "success", res.data.message || "Menu created successfully!", "toast", null, null, null, null, null, false, false, "top-center");

            setTitle("");
            setParentId(null);
            setIcon("");
            setIconSearch("");
            setItemType("section");

            await refreshMenuCache(currentUser);

            const updatedMenus = await axios.get(`${API_URL}/master/`, { withCredentials: true });
            setMenus(updatedMenus.data);
        } catch(err) {
            console.error(" Error creating menu:", err);
            notifyUser(null, "error", err.response?.data?.message || "Failed to create menu.", "toast", null, null, null, null, null, false, false, "top-center");
        }
    };

    const renderParentOptions = (items, level = 0) =>
        items.map((item) => (
            <React.Fragment key={item.id}>
                <option value={item.id}>{`${"-- ".repeat(level)}${
                    item.title
                }`}</option>
                {item.children?.length > 0 &&
                    renderParentOptions(item.children, level + 1)}
            </React.Fragment>
        ));

    return (
        <div className="max-w-xl mx-auto mt-10 bg-white shadow-md p-6 rounded-lg">
            <h2 className="text-2xl font-bold mb-4 text-center">Create Menu</h2>

            <form onSubmit={handleSubmit} className="space-y-4">
                {/* Title */}
                <div>
                    <label className="block mb-1 font-semibold">Title</label>
                    <input
                        type="text"
                        className="w-full border rounded p-2"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        required
                    />
                </div>

                {/* Item Type */}
                <div>
                    <label className="block mb-1 font-semibold">
                        Item Type
                    </label>
                    <select
                        className="w-full border rounded p-2"
                        value={itemType}
                        onChange={(e) => setItemType(e.target.value)}
                        required
                    >
                        <option value="section">Section</option>
                        <option value="menu">Menu</option>
                        <option value="item">Item</option>
                    </select>
                </div>

                {/* Icon Picker */}
                <div>
                    <label className="block mb-1 font-semibold">Icon</label>
                    <div className="relative icon-picker">
                        <input
                            type="text"
                            value={iconSearch}
                            onChange={(e) => setIconSearch(e.target.value)}
                            onFocus={() => setShowDropdown(true)}
                            placeholder="Search FontAwesome icon..."
                            className="w-full border p-2 rounded"
                        />
                        {showDropdown && filteredIcons.length > 0 && (
                            <ul className="absolute z-10 bg-white shadow-md w-full max-h-48 overflow-y-auto border border-gray-200 rounded mt-1">
                                {filteredIcons.map((iconName) => {
                                    const IconComponent = FaIcons[iconName];
                                    return (
                                        <li
                                            key={iconName}
                                            onClick={() => {
                                                setIcon(iconName);
                                                setIconSearch(iconName);
                                                setShowDropdown(false);
                                            }}
                                            className="flex items-center gap-2 px-3 py-2 hover:bg-gray-100 cursor-pointer"
                                        >
                                            {IconComponent && (
                                                <IconComponent className="text-gray-600" />
                                            )}
                                            <span>{iconName}</span>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                        {showDropdown && filteredIcons.length === 0 && (
                            <div className="absolute w-full bg-white shadow-md border border-gray-200 rounded mt-1 p-3 text-gray-500 text-sm">
                                No matching icons
                            </div>
                        )}
                    </div>

                    {/* Selected Icon Preview + Clear */}
                    {icon && (
                        <div className="mt-2 flex items-center gap-3 text-gray-800">
                            <span className="font-medium">Selected:</span>
                            {FaIcons[icon] &&
                                React.createElement(FaIcons[icon], {
                                    className: "text-xl text-blue-600",
                                })}
                            <span>{icon}</span>
                            <button
                                type="button"
                                onClick={() => {
                                    setIcon("");
                                    setIconSearch("");
                                }}
                                className="text-sm text-red-500 hover:underline"
                            >
                                ❌ Clear
                            </button>
                        </div>
                    )}
                </div>

                {/* Parent Menu */}
                <div>
                    <label className="block mb-1 font-semibold">
                        Parent Menu
                    </label>
                    <select
                        className="w-full border rounded p-2"
                        value={parentId || ""}
                        onChange={(e) =>
                            setParentId(
                                e.target.value === ""
                                    ? null
                                    : Number(e.target.value),
                            )
                        }
                    >
                        <option value="">None (Top Level)</option>
                        {renderParentOptions(menus)}
                    </select>
                </div>

                {/* Submit Button */}
                <div className="text-center">
                    <button
                        type="submit"
                        className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 shadow-md"
                    >
                        Create Menu
                    </button>
                </div>
            </form>
        </div>
    );
};

AdminCreateMenu.propTypes = {
    userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default AdminCreateMenu;
